import { useEffect, useState } from "react";
import Layout from "../../Containers/Layout";
import styles from "./StudyPrograms.module.scss";
import { Button, Modal, Table, TablePaginationConfig, Tooltip, Typography, Form, Select, Spin } from "antd";
import Search from 'antd/lib/input/Search';
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { NOTIFICATION_TYPES, openNotification } from "../Notifications/NotificationsUtils";
import { assignLearningsToCompetence, getLearningCompetences } from "../../Requests/study-program-request";
import { LearningCompetenceSimpleTypeEnum, StudyPlanStatusEnum } from "../../Api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { getAllStudyPlans } from "../../Requests/study-plan-requests";

const { Option } = Select;

const LearningCompetences = () => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const [modalPropsLearnings, setModalPropsLearnings] = useState<{ isModalVisible: boolean, title?: string, record?: any }>({
        isModalVisible: false,
        title: '',
        record: undefined
    });

    const { studyCycle, programId } = useParams<{ studyCycle: string; programId: string }>();

    const [form] = Form.useForm();
    const [searchLearningName, setLearningSearchTerm] = useState('');
    const [isLoadingLearningSearch, setIsLoadingLearningSearch] = useState(false);
    const [learnings, setLearnings] = useState<any[]>([]);
    const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(undefined);
    const selectedCompetenceType = modalPropsLearnings.record?.type as string | undefined;
    const hasImplicitTemplateResults =
        selectedCompetenceType === 'KeyCompetence'
        || selectedCompetenceType === 'GreenCompetence'
        || selectedCompetenceType === 'DigitalCompetence';

    const queryClient = useQueryClient();

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setCurrentPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);
    };

    const { data: plans } = useQuery(
        ["getAllStudyPlansForLearnings", programId],
        () => getAllStudyPlans(programId, 1, 100),
        {
            onError: () =>
                openNotification('Eroare', 'Planurile nu au putut fi încărcate!', NOTIFICATION_TYPES.ERROR),
        }
    );

    const draftPlans = plans?.data?.filter(p => p.status === StudyPlanStatusEnum.Draft) ?? [];

    useEffect(() => {
        if (!draftPlans.length) {
            setSelectedPlanId(undefined);
            return;
        }

        if (selectedPlanId && draftPlans.some(p => p.id === selectedPlanId)) {
            return;
        }

        const latestDraftPlan = [...draftPlans].sort((a, b) => (b.planYear ?? 0) - (a.planYear ?? 0))[0];
        setSelectedPlanId(latestDraftPlan.id);
    }, [draftPlans, selectedPlanId]);

    const { data, isLoading } = useQuery(
        ['getLearningCompetences', searchTerm, pageSize, currentPage, selectedPlanId],
        () => getLearningCompetences({ name: searchTerm, type: LearningCompetenceSimpleTypeEnum.Competence, page: currentPage, pageSize, programId, studyPlanId: selectedPlanId }),
        {
            enabled: !!selectedPlanId,
            onError: () =>
                openNotification('Eroare', 'Datele nu au putut fi încărcate!', NOTIFICATION_TYPES.ERROR),
        }
    );

    const fetchLearnings = async (search: string) => {
        if (!selectedPlanId) {
            setLearnings([]);
            return;
        }
        setIsLoadingLearningSearch(true);
        const result = await getLearningCompetences({ name: search, type: LearningCompetenceSimpleTypeEnum.Learning, page: 1, pageSize: 100, programId, studyPlanId: selectedPlanId });
        setLearnings(result?.data || []);
        setIsLoadingLearningSearch(false);
    };

    const handleShowLearningsDetails = async (record: any) => {
        form.setFieldsValue({ assignedLearnings: record.learnings ?? [] });
        setLearningSearchTerm('');
        await fetchLearnings('');
        setModalPropsLearnings({ isModalVisible: true, title: "Asignează rezultate învățare", record });
    };

    const handleCloseLearning = () => {
        setLearningSearchTerm('');
        setModalPropsLearnings(prev => ({ ...prev, isModalVisible: false }));
    };

    const handleUpdateLearnings = async () => {
    try {
        const learningsIds = form.getFieldValue('assignedLearnings');
        const sourceId = modalPropsLearnings.record.id;

        await assignLearningsToCompetence({
            sourceLearningId: sourceId,
            targetLearningIds: learningsIds,
        });

        await queryClient.invalidateQueries(['getLearningCompetences']);

        openNotification('Succes', 'Rezultatele învățării au fost actualizate!', NOTIFICATION_TYPES.SUCCESS);
        handleCloseLearning();
    } catch (error) {
        openNotification('Eroare', 'A apărut o eroare la actualizarea rezultatelor învățării!', NOTIFICATION_TYPES.ERROR);
    }
};

    const typeTranslations: Record<string, string> = {
        ProfessionalCompetence: 'Competență profesională',
        TransversalCompetence: 'Competență transversală',
        KeyCompetence: 'Competență cheie',
        GreenCompetence: 'Competență verde',
        DigitalCompetence: 'Competență digitală',
        Knowledge: 'Cunoștințe',
        Skill: 'Abilități',
        ResponsibilityAutonomy: 'Responsabilitate și autonomie',
    };

    const columns = [
        {
            title: 'Tip',
            dataIndex: 'type',
            key: 'type',
            render: (type: string) => typeTranslations[type] || type,
            width: '150px',
        },
        {
            title: <Search placeholder="Caută după identificator" onSearch={setSearchTerm} allowClear style={{ width: '100%' }} />,
            dataIndex: 'identificator',
            key: 'identificator',
            width: '150px',
        },
        {
            title: "Nume",
            dataIndex: 'description',
            key: 'description',
            width: '400px',
        },
        {
            title: 'Rezultatele Învățării',
            key: 'learningResults',
            render: (_: any, record: any) => (
                <Tooltip title="Asignează rezultate învățare">
                    <FontAwesomeIcon
                        icon={solid('edit')}
                        style={{
                            cursor: 'pointer',
                            height: '1rem',
                            padding: '6px 0px',
                        }}
                        onClick={() => handleShowLearningsDetails(record)}
                    />
                </Tooltip>
            ),
            width: '100px',
        }
    ];

    return (
        <div className={styles.container} style={{ display: "flex", flexDirection: "column", marginTop: "0%" }}>
            <Layout>
                <div className={styles.container}>
                    <Modal
                        open={modalPropsLearnings.isModalVisible}
                        onOk={handleUpdateLearnings}
                        onCancel={handleCloseLearning}
                        title={modalPropsLearnings.title}
                        width={"1000px"}
                        okText={t('account.confirm')}
                        cancelText={t('account.cancel')}
                        okButtonProps={{ disabled: hasImplicitTemplateResults }}
                    >
                        <Form form={form} layout="vertical">
                            {hasImplicitTemplateResults && (
                                <><Typography.Text type="secondary">
                                    Pentru competențele cheie, verzi și digitale, corelarea cu rezultatele învățării este implicită. Din acest motiv, rezultatele afișate nu pot fi editate manual.
                                </Typography.Text>
                                <br /><br />
                                </>
                            )}
                            <Form.Item name="assignedLearnings">
                                <Select
                                    mode="multiple"
                                    maxTagCount={20}
                                    allowClear
                                    showSearch
                                    disabled={hasImplicitTemplateResults}
                                    style={{ width: "100%", textAlign: "left" }}
                                    placeholder={"Căutare rezultate învățare"}
                                    filterOption={false}
                                    onSearch={value => {
                                        setLearningSearchTerm(value);
                                        fetchLearnings(value);
                                    }}
                                    notFoundContent={isLoadingLearningSearch ? <Spin size="small" /> : null}
                                >
                                    {learnings.map((learning: any) => (
                                        <Option key={learning.id} value={learning.id}>
                                            {learning.identificator} - {learning.description}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Form>
                    </Modal>

                    <Table
                        className={styles.usersTable}
                        dataSource={data?.data || []}
                        rowKey={record => record.id ?? ''}
                        columns={columns}
                        loading={isLoading}
                        onChange={handleTableChange}
                        pagination={{
                            total: data?.totalCount,
                            current: currentPage,
                            pageSize: pageSize,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50"],
                            locale: { items_per_page: t('configuration.pagination') },
                            position: ['topRight'],
                            showTotal: (total: number) => (
                                <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <div style={{ minWidth: '220px' }}>Plan cadru (doar planuri draft):</div>
                                    <Select
                                        style={{ width: '220px' }}
                                        value={selectedPlanId}
                                        placeholder="Selectează planul cadru"
                                        onChange={(value) => setSelectedPlanId(value)}
                                    >
                                        {draftPlans.map((plan) => (
                                            <Option key={plan.id} value={plan.id}>
                                                {`Plan cadru ${plan.planYear}-${plan.endPlanYear}`}
                                            </Option>
                                        ))}
                                    </Select>
                                <Tooltip title="Înapoi">
                                    <Link to={`/${studyCycle}/programe`}>
                                        <Button type="primary" icon={<ArrowLeftOutlined />} style={{ marginRight: 10 }}>
                                            Înapoi
                                        </Button>
                                    </Link>
                                </Tooltip>
                                </div>
                            ),
                        }}
                        locale={{
                            emptyText: (
                                <span>
                                    <br />

                                    <Typography>
                                        Nu există competențe înregistrate momentan pe acest plan cadru.
                                    </Typography>
                                    <br />
                                    <Select
                                        style={{ width: '220px' }}
                                        value={selectedPlanId}
                                        placeholder="Selectează planul cadru"
                                        onChange={(value) => setSelectedPlanId(value)}
                                    >
                                        {draftPlans.map((plan) => (
                                            <Option key={plan.id} value={plan.id}>
                                                {`Plan cadru ${plan.planYear}-${plan.endPlanYear}`}
                                            </Option>
                                        ))}
                                    </Select>
                                    <br />
                                    <br />

                                    <Tooltip title="Înapoi">
                                        <Link to={`/${studyCycle}/programe`}>
                                            <Button type="primary" icon={<ArrowLeftOutlined />} style={{ marginRight: 10 }}>
                                                Înapoi
                                            </Button>
                                        </Link>
                                    </Tooltip>
                                </span>
                            )
                        }}
                        scroll={{ y: 'calc(100vh - 330px)' }}
                        sticky
                    />
                </div>
            </Layout>
        </div>
    );
};

export default LearningCompetences;
